import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import {
  UserOutlined,
  LockOutlined,
  IdcardOutlined,
  CheckCircleOutlined,
  MailOutlined,
  PhoneOutlined,
  BankOutlined,
  MedicineBoxOutlined,
  GlobalOutlined,
  StarOutlined,
  ApartmentOutlined,
  SolutionOutlined,
} from '@ant-design/icons';
import { MainColor } from '../style/styleValue';
import { signup, SignupPayload } from '../api/authAPI';
import { useLang } from '../i18n';

interface FormData {
  name: string;
  englishName: string;
  birthDate: string;
  email: string;
  phone: string;
  workplace: string;
  medicalLicenseNumber: string;
  hasLicense: boolean | null;
  department: string;
  position: string;
  isSpecialist: boolean | null;
  specialistNumber: string;
  username: string;
  password: string;
  passwordConfirm: string;
}

const ko = {
  steps: ['기본정보', '계정설정'],
  title: '회원가입',
  subStep0: '가입에 필요한 기본 정보를 입력해주세요.',
  subStep1: '로그인에 사용할 계정 정보를 설정해주세요.',
  subDone: '회원가입 신청이 완료되었습니다!',
  sectionPersonal: '인적사항',
  sectionContact: '연락처',
  sectionAffiliation: '소속 및 자격',
  sectionAccount: '계정 정보',
  nameKo: '이름 (한글)',
  nameKoPh: '한글 이름을 입력하세요',
  nameEn: '이름 (영문)',
  nameEnPh: '영문 이름을 입력하세요',
  birthDate: '생년월일',
  email: '이메일',
  phone: '휴대폰 번호',
  phonePh: '010-0000-0000',
  workplace: '직장 / 학교',
  workplacePh: '소속 기관명을 입력하세요',
  department: '소속과',
  departmentPh: '예: 병리과',
  position: '직위',
  positionPh: '예: 교수, 전임의, 전공의, 학생',
  license: '의사면허번호',
  optional: '(선택)',
  hasLicense: '면허번호 있음',
  noLicense: '없음',
  licensePh: '면허번호를 입력하세요',
  specialist: '전문의 여부',
  isSpecialist: '전문의',
  notSpecialist: '해당 없음',
  specialistNumber: '전문의 번호',
  specialistNumberPh: '전문의 번호를 입력하세요',
  next: '다음 단계',
  username: '아이디',
  password: '비밀번호',
  passwordConfirm: '비밀번호 확인',
  passwordConfirmPh: '비밀번호를 다시 입력하세요',
  passwordMatch: '✓ 비밀번호가 일치합니다.',
  passwordMismatch: '✗ 비밀번호가 일치하지 않습니다.',
  summaryTitle: '입력 정보 확인',
  summaryName: '이름',
  summaryBirth: '생년월일',
  summaryEmail: '이메일',
  summaryPhone: '휴대폰',
  summaryWorkplace: '소속',
  summaryLicense: '면허번호',
  summarySpecialist: '전문의',
  none: '없음',
  yes: (num: string) => `예 (${num})`,
  no: '아니오',
  prev: '이전',
  submitting: '처리 중…',
  submit: '가입 신청하기',
  doneTitle: '신청 완료',
  doneDesc: (name: string) => (
    <>
      <strong>{name}</strong>님의 회원가입 신청이 완료되었습니다.
      <br /><br />
      관리자 검토 후 승인 완료 시 로그인 할 수 있습니다.
    </>
  ),
  goLogin: '로그인 페이지로 이동',
  haveAccount: '이미 계정이 있으신가요?',
  login: '로그인',
  err: {
    nameKoRequired: '이름(한글)을 입력해주세요.',
    nameKoInvalid: '이름(한글)을 올바르게 입력해주세요.',
    nameEnRequired: '이름(영문)을 입력해주세요.',
    nameEnInvalid: '이름(영문)을 올바르게 입력해주세요.',
    birthDateRequired: '생년월일을 입력해주세요.',
    emailRequired: '이메일을 입력해주세요.',
    emailInvalid: '이메일 형식이 올바르지 않습니다.',
    phoneRequired: '휴대폰 번호를 입력해주세요.',
    phoneInvalid: '휴대폰 번호 형식이 올바르지 않습니다.',
    workplaceRequired: '직장/학교를 입력해주세요.',
    departmentRequired: '소속과를 입력해주세요.',
    positionRequired: '직위를 입력해주세요.',
    licenseChoice: '의사면허번호 보유 여부를 선택해주세요.',
    licenseRequired: '의사면허번호를 입력해주세요.',
    specialistChoice: '전문의 여부를 선택해주세요.',
    specialistNumberRequired: '전문의 번호를 입력해주세요.',
    // 서버 응답 문구를 그대로 쓰므로 한국어는 null
    duplicateUsername: null as string | null,
    invalidInput: null as string | null,
    serverError: null as string | null,
  },
};

const en: typeof ko = {
  steps: ['Basic Info', 'Account'],
  title: 'Sign Up',
  subStep0: 'Please enter the basic information required to sign up.',
  subStep1: 'Please set up the account you will use to log in.',
  subDone: 'Your sign-up request has been submitted!',
  sectionPersonal: 'Personal Information',
  sectionContact: 'Contact',
  sectionAffiliation: 'Affiliation & Qualifications',
  sectionAccount: 'Account Information',
  nameKo: 'Name (Korean)',
  nameKoPh: 'Enter your name in Korean',
  nameEn: 'Name',
  nameEnPh: 'Enter your full name in English',
  birthDate: 'Date of Birth',
  email: 'Email',
  phone: 'Mobile Phone',
  phonePh: '+1 555 123 4567',
  workplace: 'Institution / School',
  workplacePh: 'Enter the name of your institution',
  department: 'Department',
  departmentPh: 'e.g. Pathology',
  position: 'Position',
  positionPh: 'e.g. Professor, Fellow, Resident, Student',
  license: 'Medical License Number',
  optional: '(optional)',
  hasLicense: 'I have a license number',
  noLicense: 'None',
  licensePh: 'Enter your license number',
  specialist: 'Board-Certified Specialist',
  isSpecialist: 'Specialist',
  notSpecialist: 'Not applicable',
  specialistNumber: 'Specialist Number',
  specialistNumberPh: 'Enter your specialist number',
  next: 'Next',
  username: 'Username',
  password: 'Password',
  passwordConfirm: 'Confirm Password',
  passwordConfirmPh: 'Enter your password again',
  passwordMatch: '✓ Passwords match.',
  passwordMismatch: '✗ Passwords do not match.',
  summaryTitle: 'Review Your Information',
  summaryName: 'Name',
  summaryBirth: 'Date of Birth',
  summaryEmail: 'Email',
  summaryPhone: 'Mobile',
  summaryWorkplace: 'Institution',
  summaryLicense: 'License No.',
  summarySpecialist: 'Specialist',
  none: 'None',
  yes: (num: string) => `Yes (${num})`,
  no: 'No',
  prev: 'Back',
  submitting: 'Submitting…',
  submit: 'Submit',
  doneTitle: 'Request Submitted',
  doneDesc: (name: string) => (
    <>
      The sign-up request for <strong>{name}</strong> has been submitted.
      <br /><br />
      You can log in once an administrator has reviewed and approved your request.
    </>
  ),
  goLogin: 'Go to Log In',
  haveAccount: 'Already have an account?',
  login: 'Log in',
  err: {
    nameKoRequired: 'Please enter your name in Korean.',
    nameKoInvalid: 'Please enter a valid Korean name.',
    nameEnRequired: 'Please enter your name in English.',
    nameEnInvalid: 'Please enter a valid English name.',
    birthDateRequired: 'Please enter your date of birth.',
    emailRequired: 'Please enter your email.',
    emailInvalid: 'Please enter a valid email address.',
    phoneRequired: 'Please enter your mobile phone number.',
    phoneInvalid: 'Please enter a valid phone number, including the country code (e.g. +1 555 123 4567).',
    workplaceRequired: 'Please enter your institution or school.',
    departmentRequired: 'Please enter your department.',
    positionRequired: 'Please enter your position.',
    licenseChoice: 'Please indicate whether you have a medical license number.',
    licenseRequired: 'Please enter your medical license number.',
    specialistChoice: 'Please indicate whether you are a board-certified specialist.',
    specialistNumberRequired: 'Please enter your specialist number.',
    duplicateUsername: 'This username is already taken.',
    invalidInput: 'Please check the information you entered.',
    serverError: 'An error occurred while processing your sign-up. Please try again later.',
  },
};

const Signup = () => {
  const navigate = useNavigate();
  const lang = useLang();
  const t = lang === 'en' ? en : ko;
  // 영어 화면: 한글 이름 칸이 없고(영문 이름을 이름으로 쓴다), 휴대폰은 해외 번호(국가번호 포함)도 받는다
  const isEn = lang === 'en';

  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    englishName: '',
    birthDate: '',
    email: '',
    phone: '',
    workplace: '',
    medicalLicenseNumber: '',
    hasLicense: null,
    department: '',
    position: '',
    isSpecialist: null,
    specialistNumber: '',
    username: '',
    password: '',
    passwordConfirm: '',
  });
  const [errorMsg, setErrorMsg] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDone, setIsDone] = useState(false);
  // 한국어 화면에서 한글 이름을 입력한 뒤 영어로 바꿔도 영어 화면에서는 무시한다
  const displayName = isEn ? formData.englishName.trim() : formData.name.trim();

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    let sanitized = value;

    if (['username', 'password', 'passwordConfirm'].includes(name)) {
      sanitized = value.replace(/\s/g, '');
    }
    if (name === 'phone' && isEn) {
      sanitized = value.replace(/[^0-9+\-\s()]/g, '');
    } else if (name === 'phone') {
      const digits = value.replace(/[^0-9]/g, '');
      if (digits.length <= 7) {
        sanitized = digits.replace(/^(\d{3})(\d+)/, '$1-$2');
      } else {
        sanitized = digits.replace(/^(\d{3})(\d{4})(\d+)/, '$1-$2-$3');
      }
    }
    if (name === 'nameEn') {
      sanitized = value.replace(/[^a-zA-Z\s]/g, '').toUpperCase();
    }

    setFormData(prev => ({ ...prev, [name]: sanitized }));
    if (errorMsg) setErrorMsg('');
  };

  const handleLicenseToggle = (has: boolean) => {
    setFormData(prev => ({
      ...prev,
      hasLicense: has,
      medicalLicenseNumber: has ? prev.medicalLicenseNumber : '',
    }));
    if (errorMsg) setErrorMsg('');
  };

  const handleSpecialistToggle = (is: boolean) => {
    setFormData(prev => ({
      ...prev,
      isSpecialist: is,
      specialistNumber: is ? prev.specialistNumber : '',
    }));
    if (errorMsg) setErrorMsg('');
  };

  const validateStep0 = () => {
    const e = t.err;
    if (!isEn) {
      if (!formData.name.trim()) return e.nameKoRequired;
      if (!/^[가-힣]{2,10}$/.test(formData.name)) return e.nameKoInvalid;
    }
    if (!formData.englishName.trim()) return e.nameEnRequired;
    if (!/^[A-z\s]{2,30}$/.test(formData.englishName.trim())) return e.nameEnInvalid;
    if (!formData.birthDate) return e.birthDateRequired;
    if (!formData.email.trim()) return e.emailRequired;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) return e.emailInvalid;
    if (!formData.phone.trim()) return e.phoneRequired;
    if (isEn) {
      const digitCount = formData.phone.replace(/\D/g, '').length;
      if (!/^\+?[0-9\s\-()]+$/.test(formData.phone.trim()) || digitCount < 7 || digitCount > 15) return e.phoneInvalid;
    } else if (!/\d{3}-\d{4}-\d{4}$/.test(formData.phone)) return e.phoneInvalid;
    if (!formData.workplace.trim()) return e.workplaceRequired;
    if (!formData.department.trim()) return e.departmentRequired;
    if (!formData.position.trim()) return e.positionRequired;
    if (formData.hasLicense === null) return e.licenseChoice;
    if (formData.hasLicense && !formData.medicalLicenseNumber.trim()) return e.licenseRequired;
    if (formData.isSpecialist === null) return e.specialistChoice;
    if (formData.isSpecialist && !formData.specialistNumber.trim()) return e.specialistNumberRequired;
    return '';
  };

  // 아이디, 비밀번호 유효성 검사 (필요시)
  // const validateStep1 = () => {
  //   if (!formData.username.trim()) return '아이디를 입력해주세요.';
  //   if (formData.username.length < 4) return '아이디는 4자 이상이어야 합니다.';
  //   if (!formData.password) return '비밀번호를 입력해주세요.';
  //   if (formData.password.length < 8) return '비밀번호는 8자 이상이어야 합니다.';
  //   if (!/(?=.*[a-zA-Z])(?=.*\d)/.test(formData.password)) return '비밀번호는 영문과 숫자를 포함해야 합니다.';
  //   if (formData.password !== formData.passwordConfirm) return '비밀번호가 일치하지 않습니다.';
  //   return '';
  // };

  const goNext = () => {
    // const err = step === 0 ? validateStep0() : validateStep1();
    const err = validateStep0();
    if (err) { setErrorMsg(err); return; }
    setStep(s => s + 1);
    setErrorMsg('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goPrev = () => {
    setStep(s => s - 1);
    setErrorMsg('');
  };

  const handleSubmit = async () => {
    // const err = validateStep1();
    // if (err) { setErrorMsg(err); return; }

    setIsSubmitting(true);

    const payload: SignupPayload = {
      username: formData.username,
      password: formData.password,
      // 영어 화면에는 한글 이름 칸이 없으므로 영문 이름을 이름으로 쓴다
      name: displayName,
      englishName: formData.englishName.trim(),
      email: formData.email,
      birthDate: formData.birthDate,
      phone: formData.phone.trim(),
      workplace: formData.workplace,
      department: formData.department.trim(),
      position: formData.position.trim(),
      isSpecialist: formData.isSpecialist === true,
      ...(formData.hasLicense && formData.medicalLicenseNumber.trim()
        ? { medicalLicenseNumber: formData.medicalLicenseNumber.trim() }
        : {}),
      ...(formData.isSpecialist && formData.specialistNumber.trim()
        ? { specialistNumber: formData.specialistNumber.trim() }
        : {}),
    };

    const result = await signup(payload);

    setIsSubmitting(false);

    if (result.success) {
      setIsDone(true);
      return;
    }

    // 중복 아이디 → 계정 단계로 자동 이동
    if (result.status === 409) {
      setStep(1);
    }
    // 서버 문구는 한국어라 영어 화면에서는 상태 코드별 문구로 바꿔 보여준다
    const translated =
      result.status === 409 ? t.err.duplicateUsername
        : result.status === 400 ? t.err.invalidInput
          : t.err.serverError;
    setErrorMsg(translated ?? result.message);
  };

  return (
    <PageWrapper>
      <FormCard>

        <HeaderArea>
          <LogoMark>+</LogoMark>
          <Title>{t.title}</Title>
          <SubText>
            {!isDone && step === 0 && t.subStep0}
            {!isDone && step === 1 && t.subStep1}
            {isDone && t.subDone}
          </SubText>
        </HeaderArea>

        {!isDone && (
          <StepIndicator>
            {t.steps.map((label, i) => (
              <React.Fragment key={label}>
                <StepItem $active={step === i} $done={step > i}>
                  <StepCircle $active={step === i} $done={step > i}>
                    {step > i ? '✓' : i + 1}
                  </StepCircle>
                  <StepLabel $active={step === i}>{label}</StepLabel>
                </StepItem>
                {i < t.steps.length - 1 && <StepLine $done={step > i} />}
              </React.Fragment>
            ))}
          </StepIndicator>
        )}

        {!isDone && step === 0 && (
          <StepContent>
            <SectionLabel>{t.sectionPersonal}</SectionLabel>

            <FieldRow style={isEn ? { gridTemplateColumns: '1fr' } : undefined}>
              {!isEn && (
                <FieldGroup>
                  <FieldLabel>{t.nameKo}</FieldLabel>
                  <InputWrapper>
                    <IdcardOutlined className="icon" />
                    <Input
                      type="text"
                      name="name"
                      placeholder={t.nameKoPh}
                      value={formData.name}
                      onChange={handleChange}
                    />
                  </InputWrapper>
                </FieldGroup>
              )}
              <FieldGroup>
                <FieldLabel>{t.nameEn}</FieldLabel>
                <InputWrapper>
                  <GlobalOutlined className="icon" />
                  <Input
                    type="text"
                    name="englishName"
                    placeholder={t.nameEnPh}
                    value={formData.englishName}
                    onChange={handleChange}
                  />
                </InputWrapper>
              </FieldGroup>
            </FieldRow>

            <FieldGroup>
              <FieldLabel>{t.birthDate}</FieldLabel>
              <InputWrapper>
                <Input
                  type="date"
                  name="birthDate"
                  value={formData.birthDate}
                  onChange={handleChange}
                  max={new Date().toISOString().split('T')[0]}
                  style={{ paddingLeft: '14px' }}
                />
              </InputWrapper>
            </FieldGroup>

            <SectionLabel style={{ marginTop: '4px' }}>{t.sectionContact}</SectionLabel>

            <FieldGroup>
              <FieldLabel>{t.email}</FieldLabel>
              <InputWrapper>
                <MailOutlined className="icon" />
                <Input
                  type="email"
                  name="email"
                  placeholder="example@email.com"
                  value={formData.email}
                  onChange={handleChange}
                />
              </InputWrapper>
            </FieldGroup>

            <FieldGroup>
              <FieldLabel>{t.phone}</FieldLabel>
              <InputWrapper>
                <PhoneOutlined className="icon" />
                <Input
                  type="tel"
                  name="phone"
                  placeholder={t.phonePh}
                  value={formData.phone}
                  onChange={handleChange}
                  maxLength={isEn ? 20 : 13}
                />
              </InputWrapper>
            </FieldGroup>

            <SectionLabel style={{ marginTop: '4px' }}>{t.sectionAffiliation}</SectionLabel>

            <FieldGroup>
              <FieldLabel>{t.workplace}</FieldLabel>
              <InputWrapper>
                <BankOutlined className="icon" />
                <Input
                  type="text"
                  name="workplace"
                  placeholder={t.workplacePh}
                  value={formData.workplace}
                  onChange={handleChange}
                />
              </InputWrapper>
            </FieldGroup>

            <FieldRow>
              <FieldGroup>
                <FieldLabel>{t.department}</FieldLabel>
                <InputWrapper>
                  <ApartmentOutlined className="icon" />
                  <Input
                    type="text"
                    name="department"
                    placeholder={t.departmentPh}
                    value={formData.department}
                    onChange={handleChange}
                    maxLength={100}
                  />
                </InputWrapper>
              </FieldGroup>
              <FieldGroup>
                <FieldLabel>{t.position}</FieldLabel>
                <InputWrapper>
                  <SolutionOutlined className="icon" />
                  <Input
                    type="text"
                    name="position"
                    placeholder={t.positionPh}
                    value={formData.position}
                    onChange={handleChange}
                    maxLength={100}
                  />
                </InputWrapper>
              </FieldGroup>
            </FieldRow>

            <FieldGroup>
              <FieldLabel>{t.license} <Optional>{t.optional}</Optional></FieldLabel>
              <ToggleRow>
                <ToggleBtn
                  $active={formData.hasLicense === true}
                  onClick={() => handleLicenseToggle(true)}
                  type="button"
                >
                  {t.hasLicense}
                </ToggleBtn>
                <ToggleBtn
                  $active={formData.hasLicense === false}
                  onClick={() => handleLicenseToggle(false)}
                  type="button"
                >
                  {t.noLicense}
                </ToggleBtn>
              </ToggleRow>
              {formData.hasLicense === true && (
                <InputWrapper style={{ marginTop: '10px' }}>
                  <MedicineBoxOutlined className="icon" />
                  <Input
                    type="text"
                    name="medicalLicenseNumber"
                    placeholder={t.licensePh}
                    value={formData.medicalLicenseNumber}
                    onChange={handleChange}
                  />
                </InputWrapper>
              )}
            </FieldGroup>

            <FieldGroup>
              <FieldLabel>{t.specialist}</FieldLabel>
              <ToggleRow>
                <ToggleBtn
                  $active={formData.isSpecialist === true}
                  onClick={() => handleSpecialistToggle(true)}
                  type="button"
                >
                  {t.isSpecialist}
                </ToggleBtn>
                <ToggleBtn
                  $active={formData.isSpecialist === false}
                  onClick={() => handleSpecialistToggle(false)}
                  type="button"
                >
                  {t.notSpecialist}
                </ToggleBtn>
              </ToggleRow>
            </FieldGroup>

            {formData.isSpecialist === true && (
              <FieldGroup>
                <FieldLabel>{t.specialistNumber}</FieldLabel>
                <InputWrapper>
                  <StarOutlined className="icon" />
                  <Input
                    type="text"
                    name="specialistNumber"
                    placeholder={t.specialistNumberPh}
                    value={formData.specialistNumber}
                    onChange={handleChange}
                  />
                </InputWrapper>
              </FieldGroup>
            )}

            {errorMsg && <ErrorMessage>{errorMsg}</ErrorMessage>}

            <NextButton type="button" onClick={goNext}>
              {t.next}
            </NextButton>
          </StepContent>
        )}

        {!isDone && step === 1 && (
          <StepContent>
            <SectionLabel>{t.sectionAccount}</SectionLabel>

            <FieldGroup>
              <FieldLabel>{t.username}</FieldLabel>
              <InputWrapper>
                <UserOutlined className="icon" />
                <Input
                  type="text"
                  name="username"
                  placeholder={t.username}
                  value={formData.username}
                  onChange={handleChange}
                  autoComplete="username"
                />
              </InputWrapper>
            </FieldGroup>

            <FieldGroup>
              <FieldLabel>{t.password}</FieldLabel>
              <InputWrapper>
                <LockOutlined className="icon" />
                <Input
                  type="password"
                  name="password"
                  placeholder={t.password}
                  value={formData.password}
                  onChange={handleChange}
                  autoComplete="new-password"
                />
              </InputWrapper>
            </FieldGroup>

            <FieldGroup>
              <FieldLabel>{t.passwordConfirm}</FieldLabel>
              <InputWrapper>
                <CheckCircleOutlined className="icon" />
                <Input
                  type="password"
                  name="passwordConfirm"
                  placeholder={t.passwordConfirmPh}
                  value={formData.passwordConfirm}
                  onChange={handleChange}
                  autoComplete="new-password"
                  $match={
                    formData.passwordConfirm
                      ? formData.password === formData.passwordConfirm
                      : null
                  }
                />
              </InputWrapper>
              {formData.passwordConfirm && (
                <MatchText $ok={formData.password === formData.passwordConfirm}>
                  {formData.password === formData.passwordConfirm
                    ? t.passwordMatch
                    : t.passwordMismatch}
                </MatchText>
              )}
            </FieldGroup>

            <SummaryBox>
              <SummaryTitle>{t.summaryTitle}</SummaryTitle>
              <SummaryGrid>
                {[
                  {
                    k: t.summaryName,
                    v: isEn ? formData.englishName : `${formData.name} (${formData.englishName})`,
                  },
                  { k: t.summaryBirth, v: formData.birthDate },
                  { k: t.summaryEmail, v: formData.email },
                  { k: t.summaryPhone, v: formData.phone },
                  { k: t.summaryWorkplace, v: formData.workplace },
                  { k: t.department, v: formData.department },
                  { k: t.position, v: formData.position },
                  { k: t.summaryLicense, v: formData.medicalLicenseNumber || t.none },
                  {
                    k: t.summarySpecialist,
                    v: formData.isSpecialist ? t.yes(formData.specialistNumber) : t.no,
                  },
                ].map(({ k, v }) => (
                  <SummaryRow key={k}>
                    <SummaryKey>{k}</SummaryKey>
                    <SummaryVal>{v}</SummaryVal>
                  </SummaryRow>
                ))}
              </SummaryGrid>
            </SummaryBox>

            {errorMsg && <ErrorMessage>{errorMsg}</ErrorMessage>}

            <ButtonRow>
              <PrevButton type="button" onClick={goPrev} disabled={isSubmitting}>
                {t.prev}
              </PrevButton>
              <SubmitButton
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? t.submitting : t.submit}
              </SubmitButton>
            </ButtonRow>
          </StepContent>
        )}

        {isDone && (
          <StepContent>
            <CompleteBox>
              <CompleteIcon>✓</CompleteIcon>
              <CompleteTitle>{t.doneTitle}</CompleteTitle>
              <CompleteDesc>{t.doneDesc(displayName)}</CompleteDesc>
              <CompleteButton onClick={() => navigate('/login')}>
                {t.goLogin}
              </CompleteButton>
            </CompleteBox>
          </StepContent>
        )}

        {!isDone && (
          <FooterLinks>
            {t.haveAccount}{' '}
            <span onClick={() => navigate('/login')}>{t.login}</span>
          </FooterLinks>
        )}
      </FormCard>
    </PageWrapper>
  );
};

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const PageWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  width: 100%;
  min-height: 100%;
  padding: 40px 20px 60px;
  background: #f0f4f8;
`;

const FormCard = styled.div`
  width: 100%;
  max-width: 540px;
  background: #fff;
  padding: 40px;
  border-radius: 16px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.1);
  border: 1px solid #e8edf2;
  display: flex;
  flex-direction: column;
  gap: 28px;
  animation: ${fadeIn} 0.4s ease;

  @media (max-width: 600px) {
    padding: 28px 20px;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  }
`;

const HeaderArea = styled.div`text-align: center;`;

const LogoMark = styled.div`
  width: 48px;
  height: 48px;
  background: ${MainColor};
  color: #fff;
  font-size: 28px;
  font-weight: 700;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #17375f;
  margin-bottom: 6px;
`;

const SubText = styled.p`
  font-size: 14px;
  color: #888;
  line-height: 1.5;
`;

const StepIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StepItem = styled.div<{ $active: boolean; $done: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  opacity: ${p => (p.$active || p.$done ? 1 : 0.4)};
  transition: opacity 0.3s;
`;

const StepCircle = styled.div<{ $active: boolean; $done: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  transition: all 0.3s;
  ${p => p.$done   && css`background: #52c41a; color: #fff; border: none;`}
  ${p => p.$active && !p.$done && css`background: ${MainColor}; color: #fff; border: none;`}
  ${p => !p.$active && !p.$done && css`background: #fff; color: #aaa; border: 2px solid #ddd;`}
`;

const StepLabel = styled.span<{ $active: boolean }>`
  font-size: 11px;
  font-weight: ${p => (p.$active ? 700 : 400)};
  color: ${p => (p.$active ? MainColor : '#aaa')};
  white-space: nowrap;
`;

const StepLine = styled.div<{ $done: boolean }>`
  width: 80px;
  height: 2px;
  margin: 0 8px;
  margin-bottom: 20px;
  background: ${p => (p.$done ? '#52c41a' : '#e0e0e0')};
  transition: background 0.3s;

  @media (max-width: 400px) { width: 40px; }
`;

const StepContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  animation: ${fadeIn} 0.3s ease;
`;

const SectionLabel = styled.h3`
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #17375f;
  padding-bottom: 8px;
  border-bottom: 2px solid #17375f;
  margin: 0;
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldLabel = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: #555;
  letter-spacing: 0.02em;
`;

const Optional = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: #aaa;
  margin-left: 4px;
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;

  .icon {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: #a0a0a0;
    font-size: 16px;
    transition: color 0.3s;
    z-index: 1;
  }

  &:focus-within .icon { color: #17375f; }
`;

const Input = styled.input<{ $match?: boolean | null }>`
  width: 100%;
  padding: 12px 14px 12px 42px;
  font-size: 14px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: #fafafa;
  outline: none;
  transition: all 0.2s;
  color: #333;

  &::placeholder { color: #bbb; }

  &:focus {
    background: #fff;
    border-color: #17375f;
    box-shadow: 0 0 0 3px rgba(23, 55, 95, 0.08);
  }

  ${p => p.$match === true  && css`border-color: #52c41a;`}
  ${p => p.$match === false && css`border-color: #ff4d4f;`}
`;

const MatchText = styled.span<{ $ok: boolean }>`
  font-size: 12px;
  color: ${p => (p.$ok ? '#52c41a' : '#ff4d4f')};
`;

const StrengthWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
`;
const StrengthTrack = styled.div`
  flex: 1;
  height: 4px;
  background: #eee;
  border-radius: 2px;
  overflow: hidden;
`;
const StrengthFill = styled.div`
  height: 100%;
  border-radius: 2px;
  transition: all 0.3s;
`;
const StrengthLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  min-width: 24px;
`;

const ToggleRow = styled.div`display: flex; gap: 8px;`;

const ToggleBtn = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 10px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: 2px solid ${p => (p.$active ? '#17375f' : '#ddd')};
  background: ${p => (p.$active ? '#17375f' : '#fff')};
  color: ${p => (p.$active ? '#fff' : '#888')};

  &:hover { border-color: #17375f; }
`;

const SummaryBox = styled.div`
  background: #f6f8fb;
  border-radius: 10px;
  padding: 16px;
  border: 1px solid #e4eaf2;
`;
const SummaryTitle = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #17375f;
  margin-bottom: 10px;
`;
const SummaryGrid = styled.div`display: flex; flex-direction: column; gap: 6px;`;
const SummaryRow = styled.div`display: flex; gap: 8px;`;
const SummaryKey = styled.span`font-size: 12px; color: #888; min-width: 70px; flex-shrink: 0;`;
const SummaryVal = styled.span`font-size: 12px; color: #333; font-weight: 500; word-break: break-all;`;

const ErrorMessage = styled.div`
  color: #ff4d4f;
  font-size: 13px;
  text-align: center;
  background: #fff1f0;
  padding: 10px;
  border-radius: 8px;
  border: 1px solid #ffa39e;
`;

const ButtonRow = styled.div`display: flex; gap: 10px; margin-top: 4px;`;

const PrevButton = styled.button`
  flex: 1;
  padding: 13px;
  border-radius: 8px;
  border: 1.5px solid #ddd;
  background: #fff;
  color: #666;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) { border-color: #17375f; color: #17375f; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const NextButton = styled.button`
  width: 100%;
  padding: 14px;
  border-radius: 8px;
  background: ${MainColor};
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 4px;

  &:hover { background: #102a4a; transform: translateY(-1px); }
  &:active { transform: translateY(0); }
`;

const SubmitButton = styled.button`
  flex: 2;
  padding: 13px;
  border-radius: 8px;
  background: ${MainColor};
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  border: none;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) { background: #102a4a; transform: translateY(-1px); }
  &:active:not(:disabled) { transform: translateY(0); }
  &:disabled { background: #b0bec5; cursor: not-allowed; }
`;

const CompleteBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 20px 0;
`;

const CompleteIcon = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: #f6ffed;
  border: 3px solid #52c41a;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  color: #52c41a;
  font-weight: 700;
`;

const CompleteTitle = styled.div`font-size: 22px; font-weight: 700; color: #17375f;`;

const CompleteDesc = styled.p`
  font-size: 14px;
  color: #666;
  text-align: center;
  line-height: 1.8;
  margin: 0;

  strong { color: #17375f; }
`;

const CompleteButton = styled.button`
  padding: 14px 36px;
  border-radius: 8px;
  background: ${MainColor};
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  border: none;
  cursor: pointer;
  transition: all 0.2s;

  &:hover { background: #102a4a; }
`;

const FooterLinks = styled.div`
  text-align: center;
  font-size: 13px;
  color: #888;

  span {
    color: #17375f;
    font-weight: 700;
    cursor: pointer;
    margin-left: 4px;
    text-decoration: underline;

    &:hover { color: #0050b3; }
  }
`;

export default Signup;